import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import DataCard from '../ui/DataCard';

/**
 * CourseSessionsTab - Sessions Tab in der CourseView
 * 
 * Zeigt aktive und beendete Sessions eines Kurses mit:
 * - Filter nach Status (aktiv/beendet/archiviert)
 * - Session-Karten mit Aktionen
 */
export default function CourseSessionsTab({
  instances,
  activeInstances,
  onEndSession,
  onArchiveSession,
  onDeleteSession
}) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('active');

  // Get ended sessions
  const endedSessions = useMemo(() => {
    return instances
      .filter(i => !i.isActive && i.status !== 'active' && !i.archived)
      .sort((a, b) => (b.endedAt || b.createdAt) - (a.endedAt || a.createdAt));
  }, [instances]);

  // Get archived sessions
  const archivedSessions = useMemo(() => {
    return instances
      .filter(i => i.archived)
      .sort((a, b) => (b.endedAt || b.createdAt) - (a.endedAt || a.createdAt));
  }, [instances]);

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format duration
  const formatDuration = (startTime, endTime) => {
    if (!startTime) return '';
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diff = Math.floor((end - start) / 1000);
    
    const minutes = Math.floor(diff / 60);
    if (minutes < 60) return `${minutes} Min`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  return (
    <div className="space-y-6">
      {/* Filter Buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setFilter('active')}
          className={`px-4 py-2 text-sm font-medium rounded-lg theme-transition ${
            filter === 'active'
              ? 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          Aktiv ({activeInstances.length})
        </button>
        <button
          onClick={() => setFilter('ended')}
          className={`px-4 py-2 text-sm font-medium rounded-lg theme-transition ${
            filter === 'ended'
              ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          Beendet ({endedSessions.length})
        </button>
        <button
          onClick={() => setFilter('archived')}
          className={`px-4 py-2 text-sm font-medium rounded-lg theme-transition ${
            filter === 'archived'
              ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
        >
          Archiviert ({archivedSessions.length})
        </button>
      </div>

      {/* Active Sessions */}
      {filter === 'active' && (
        <div className="space-y-3">
          {activeInstances.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <span className="text-5xl mb-4 block">▶️</span>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Keine aktiven Sessions
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Starte ein Quiz aus einer Vorlage, um eine Session zu beginnen
              </p>
            </div>
          ) : (
            activeInstances.map((session) => (
              <DataCard
                key={session.id}
                variant="compact"
                color="#EF4444"
                title={session.templateName || 'Quiz Session'}
                subtitle={`Code: ${session.accessCode} • ${session.participants?.length || 0} Teilnehmer • ${formatDuration(session.startedAt)}`}
                badges={[{ label: 'Live', variant: 'danger', pulse: true }]}
                menuActions={[
                  { label: 'Live öffnen', onClick: () => navigate(`/live/${session.id}`) },
                  { label: 'Ergebnisse', onClick: () => navigate(`/results/${session.id}`) },
                  { label: 'Beenden', variant: 'danger', onClick: () => onEndSession(session.id) },
                  { label: 'Session löschen', variant: 'danger', onClick: () => onDeleteSession(session.id) }
                ]}
                onClick={() => navigate(`/live/${session.id}`)}
              />
            ))
          )}
        </div>
      )}

      {/* Ended Sessions */}
      {filter === 'ended' && (
        <div className="space-y-3">
          {endedSessions.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <span className="text-5xl mb-4 block">📊</span>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Keine beendeten Sessions
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Beendete Quiz-Sessions werden hier angezeigt
              </p>
            </div>
          ) : (
            endedSessions.map((session) => (
              <DataCard
                key={session.id}
                variant="compact"
                title={session.templateName || 'Quiz Session'}
                subtitle={`${formatDate(session.endedAt || session.createdAt)} • ${session.participants?.length || 0} Teilnehmer`}
                stats={[
                  { value: session.averageScore ? `${Math.round(session.averageScore)}%` : 'N/A', label: 'Score' },
                  { value: formatDuration(session.startedAt, session.endedAt), label: 'Dauer' }
                ]}
                menuActions={[
                  { label: 'Ergebnisse ansehen', onClick: () => navigate(`/results/${session.id}`) },
                  { label: 'Als CSV exportieren', onClick: () => alert('Export wird noch implementiert') },
                  { label: 'Archivieren', onClick: () => onArchiveSession(session.id) },
                  { label: 'Session löschen', variant: 'danger', onClick: () => onDeleteSession(session.id) }
                ]}
                onClick={() => navigate(`/results/${session.id}`)}
              />
            ))
          )}
        </div>
      )}

      {/* Archived Sessions */}
      {filter === 'archived' && (
        <div className="space-y-3">
          {archivedSessions.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <span className="text-5xl mb-4 block">📦</span>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Keine archivierten Sessions
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Archivierte Sessions werden hier angezeigt
              </p>
            </div>
          ) : (
            archivedSessions.map((session) => (
              <DataCard
                key={session.id}
                variant="compact"
                title={session.templateName || 'Quiz Session'}
                subtitle={`${formatDate(session.endedAt || session.createdAt)} • ${session.participants?.length || 0} Teilnehmer`}
                stats={[
                  { value: session.averageScore ? `${Math.round(session.averageScore)}%` : 'N/A', label: 'Score' }
                ]}
                menuActions={[
                  { label: 'Ergebnisse ansehen', onClick: () => navigate(`/results/${session.id}`) },
                  { label: 'Session löschen', variant: 'danger', onClick: () => onDeleteSession(session.id) }
                ]}
                onClick={() => navigate(`/results/${session.id}`)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

CourseSessionsTab.propTypes = {
  instances: PropTypes.array.isRequired,
  activeInstances: PropTypes.array.isRequired,
  onEndSession: PropTypes.func.isRequired,
  onArchiveSession: PropTypes.func.isRequired,
  onDeleteSession: PropTypes.func.isRequired
};
